import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Building2, Users, UserCircle, ChevronDown, Shield, Stethoscope, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const PortalPicker = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentPortal, setCurrentPortal] = useState(
    localStorage.getItem('currentPortal') || (user?.role === 'admin' ? 'admin' : user?.role === 'referring_doctor' ? 'provider' : 'patient')
  );

  const portals = [
    {
      id: 'admin',
      name: 'Admin Portal',
      description: 'Platform management and analytics',
      icon: <Shield className="h-5 w-5" />,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      available: user?.role === 'admin'
    },
    {
      id: 'provider',
      name: 'Provider Portal',
      description: 'Manage referrals and patients',
      icon: <Stethoscope className="h-5 w-5" />,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      available: user?.role === 'admin' || user?.role === 'referring_doctor'
    },
    {
      id: 'patient',
      name: 'Patient Portal',
      description: 'View appointments and records',
      icon: <User className="h-5 w-5" />,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      available: user?.role === 'admin' || user?.role === 'patient'
    }
  ];

  const getCurrentPortal = () => {
    return portals.find(p => p.id === currentPortal) || portals[0];
  };

  const handlePortalSwitch = (portalId) => {
    setCurrentPortal(portalId);
    localStorage.setItem('currentPortal', portalId);
    
    // Navigate to appropriate dashboard
    navigate('/dashboard');
    
    // Trigger a page reload to update navigation
    window.location.reload();
  };

  const availablePortals = portals.filter(p => p.available);
  const current = getCurrentPortal();

  // If user only has access to one portal, don't show the picker
  if (availablePortals.length <= 1) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="min-w-[200px] justify-between border-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A1628]">
          <div className="flex items-center space-x-2">
            <div className={`p-1 rounded ${current.bgColor}`}>
              {current.icon}
            </div>
            <div className="text-left">
              <div className="text-sm font-semibold">{current.name}</div>
            </div>
          </div>
          <ChevronDown className="h-4 w-4 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[280px]">
        <DropdownMenuLabel>Switch Portal</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {availablePortals.map((portal) => (
          <DropdownMenuItem
            key={portal.id}
            onClick={() => handlePortalSwitch(portal.id)}
            className={`p-3 cursor-pointer ${currentPortal === portal.id ? 'bg-gray-100' : ''}`}
          >
            <div className="flex items-start space-x-3 w-full">
              <div className={`p-2 rounded ${portal.bgColor} ${portal.color}`}>
                {portal.icon}
              </div>
              <div className="flex-1">
                <div className="font-semibold text-sm">{portal.name}</div>
                <div className="text-xs text-gray-600 mt-1">{portal.description}</div>
              </div>
              {currentPortal === portal.id && (
                <div className="text-[#D4AF37]">✓</div>
              )}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default PortalPicker;
